import { useEffect, useState, type ReactNode } from 'react';
import { api, type ParentAssetMatch } from '../api/client';
import { Badge } from './ui';

type PickerView =
  | { kind: 'search' }
  | { kind: 'children'; node: ParentAssetMatch };

/**
 * Find a parent asset by name, then drill into its children (Building ->
 * Group -> ...). Shared by Field Tools commissioning and Virtual Meters.
 *
 * `realmMode`: 'locked' always searches `realm` and offers no way to widen it
 * (Virtual Meters: a virtual meter's parent must be in its own realm; the
 * server enforces the same for billing:admin). 'toggle-on' / 'toggle-off' show
 * a "Restrict to realm" checkbox that starts checked / unchecked
 * (commissioning). With `realm` null there is no checkbox and every realm is
 * searched.
 */
export function ParentAssetPicker({
  description,
  initialQuery = '',
  realm,
  realmMode,
  pickLabel,
  busy,
  onCancel,
  onPick,
}: {
  description: ReactNode;
  initialQuery?: string;
  realm: string | null;
  realmMode: 'locked' | 'toggle-on' | 'toggle-off';
  pickLabel: string;
  busy: boolean;
  onCancel: () => void;
  onPick: (p: ParentAssetMatch) => void;
}) {
  const [query, setQuery] = useState(initialQuery);
  const [restrictRealm, setRestrictRealm] = useState(realmMode !== 'toggle-off');
  const [view, setView] = useState<PickerView>({ kind: 'search' });
  const [searchResults, setSearchResults] = useState<ParentAssetMatch[]>([]);
  const [children, setChildren] = useState<ParentAssetMatch[]>([]);
  const [busyList, setBusyList] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  async function runSearch(q: string) {
    if (!q.trim()) {
      setSearchResults([]);
      return;
    }
    setBusyList(true);
    setListError(null);
    try {
      const r = await api.searchParentAssets(q.trim(), restrictRealm && realm ? realm : undefined);
      setSearchResults(r.data);
    } catch (err) {
      setListError((err as Error).message);
    } finally {
      setBusyList(false);
    }
  }

  async function browseInto(node: ParentAssetMatch) {
    setBusyList(true);
    setListError(null);
    setView({ kind: 'children', node });
    try {
      const r = await api.getAssetChildren(node.asset_id);
      setChildren(r.data);
    } catch (err) {
      setListError((err as Error).message);
      setChildren([]);
    } finally {
      setBusyList(false);
    }
  }

  function backToSearch() {
    setView({ kind: 'search' });
    setChildren([]);
    setListError(null);
  }

  useEffect(() => {
    if (initialQuery) runSearch(initialQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visibleResults = view.kind === 'search' ? searchResults : children;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      {/* Modal-container precedent — plain bg-surface-raised div, not <Card>. */}
      <div className="bg-surface-raised rounded-xl shadow-xl max-w-xl w-full overflow-hidden">
        <div className="px-6 py-4 border-b border-border-token bg-gradient-to-r from-primary-50/60 dark:from-primary-900/40 to-transparent">
          <div className="flex items-center gap-2">
            <SearchIcon /> <h3 className="font-semibold text-content">Find parent asset</h3>
          </div>
          {description}
        </div>

        {view.kind === 'search' ? (
          <div className="px-6 py-4 space-y-3">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') runSearch(query); }}
                placeholder="Search by building / site / group name…"
                className="flex-1 rounded-md border-border-token text-sm"
              />
              <button
                type="button"
                onClick={() => runSearch(query)}
                disabled={busyList}
                className="px-3 py-1.5 text-sm bg-surface-sunken rounded hover:bg-border-token disabled:opacity-50"
              >
                {busyList ? '…' : 'Search'}
              </button>
            </div>
            {realm && (realmMode === 'locked' ? (
              <p className="text-xs text-content-muted">
                Searching realm <code className="bg-border-token px-1 rounded">{realm}</code>
              </p>
            ) : (
              <label className="text-xs text-content-muted inline-flex items-center gap-1.5">
                <input
                  type="checkbox"
                  checked={restrictRealm}
                  onChange={(e) => setRestrictRealm(e.target.checked)}
                />
                Restrict to realm <code className="bg-border-token px-1 rounded">{realm}</code>
              </label>
            ))}
            {listError && (
              // Error message — must stay legible. Same severity-box pattern
              // (dark:bg-red-900/dark:border-red-800/dark:text-red-100).
              <div className="text-xs text-red-700 dark:text-red-100 bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-800 rounded p-2">{listError}</div>
            )}
          </div>
        ) : (
          <div className="px-6 py-3 bg-surface-sunken border-b border-border-token flex items-center gap-2">
            <button
              type="button"
              onClick={backToSearch}
              className="text-xs px-2 py-1 rounded border border-border-token bg-surface-raised hover:bg-surface-sunken inline-flex items-center gap-1"
              title="Back to search"
            >
              ← Back
            </button>
            <div className="flex-1 min-w-0 text-xs text-content inline-flex items-center gap-1 flex-wrap">
              <BuildingIcon />
              {view.node.parent_path.map((p) => (
                <span key={p.id} className="inline-flex items-center">
                  <span className="text-content-muted">{p.name}</span>
                  <span className="text-content-muted mx-1">›</span>
                </span>
              ))}
              <span className="font-medium text-content">{view.node.name}</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-border-token text-content">{view.node.asset_type}</span>
            </div>
            <button
              type="button"
              onClick={() => onPick(view.node)}
              disabled={busy}
              className="px-2.5 py-1 text-xs font-medium bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 shrink-0"
              title="Use this asset as the parent"
            >
              {busy ? '…' : pickLabel}
            </button>
          </div>
        )}

        <ul className="divide-y divide-border-token max-h-96 overflow-y-auto border-t border-border-token">
          {busyList && (
            <li className="px-6 py-4 text-sm text-content-muted text-center inline-flex items-center justify-center gap-2 w-full">
              <Spinner /> Loading…
            </li>
          )}
          {!busyList && visibleResults.length === 0 && (
            <li className="px-6 py-6 text-sm text-content-muted text-center">
              {view.kind === 'search'
                ? (query ? 'No matches.' : 'Type a name and search.')
                : 'No child assets.'}
            </li>
          )}
          {!busyList && visibleResults.map(p => (
            <li key={`${p.realm}:${p.asset_id}`} className="px-6 py-3 hover:bg-surface-sunken">
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Promoted to text-content (not text-content-muted): the
                        candidate parent asset's name is exactly what a
                        technician/admin reads to pick the right one. */}
                    <p className="text-sm font-semibold text-content">{p.name}</p>
                    <RealmPill realm={p.realm} />
                    <span className="text-[11px] px-1.5 py-0.5 rounded bg-border-token text-content">{p.asset_type}</span>
                  </div>
                  {p.parent_path.length > 0 ? (
                    <p className="text-xs text-content-muted mt-1 inline-flex items-center gap-1">
                      <BuildingIcon />
                      {p.parent_path.map((node, i) => (
                        <span key={node.id}>
                          {i > 0 && <span className="text-content-muted mx-1">›</span>}
                          <span className="text-content-muted">{node.name}</span>
                        </span>
                      ))}
                    </p>
                  ) : (
                    <p className="text-xs text-content-muted mt-1 italic">top-level</p>
                  )}
                  <p className="text-[10px] text-content-muted font-mono mt-0.5">{p.asset_id}</p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    type="button"
                    onClick={() => browseInto(p)}
                    disabled={busy || busyList}
                    className="px-2 py-1.5 text-xs border border-border-token text-content rounded-md hover:bg-surface-sunken disabled:opacity-50"
                    title="Browse child assets (e.g. Building → Group)"
                  >
                    ▸
                  </button>
                  <button
                    type="button"
                    onClick={() => onPick(p)}
                    disabled={busy}
                    className="px-3 py-1.5 text-xs font-medium bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                  >
                    {busy ? '…' : pickLabel}
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
        <div className="px-6 py-3 border-t border-border-token flex justify-end">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="px-3 py-1.5 text-sm text-content-muted hover:text-content disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export function BuildingIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-content-muted shrink-0" aria-hidden="true">
      <rect x="3" y="3" width="18" height="18" rx="1" />
      <path d="M9 22V12h6v10" />
      <path d="M8 6h.01M12 6h.01M16 6h.01M8 10h.01M12 10h.01M16 10h.01" />
    </svg>
  );
}

export function SearchIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-current shrink-0" aria-hidden="true">
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4.3-4.3" />
    </svg>
  );
}

export function Spinner() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="animate-spin text-current shrink-0" aria-hidden="true">
      <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
      <path d="M22 12a10 10 0 0 1-10 10" strokeLinecap="round" />
    </svg>
  );
}

// Routed through ui/Badge (neutral tone). bg-slate-100/text-slate-700 was a
// plain identifying tag, not a severity signal — 'neutral' is the closest
// available tone (hue-shift slate->stone, both read as "gray" in this app's
// warm palette; same class of normalization as the xlsx/ocr/body pills
// below). Badge's own rounded-full/text-xs sizing is accepted rather than
// preserved, matching the established Badge-adoption convention elsewhere
// in this migration.
export function RealmPill({ realm }: { realm: string }) {
  if (!realm) return null;
  return (
    <Badge tone="neutral" className="uppercase tracking-wide">
      {realm}
    </Badge>
  );
}
